import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { selectUserInformations } from "../../features/authSlice";
import { API } from "../../shared/api/Api";
import { showToast } from "../../utils/utils";
import MaterialTable from "../../components/Tables/MaterialTable";

const DEBOUNCE_DELAY = 700;

const MaterialPage = () => {
  const [reFetchData, setReFetchData] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loader, setLoader] = useState(true);
  const [orderBy, setOrderBy] = useState("created_at");
  const [orderDir, setOrderDir] = useState("DESC");

  const params = useParams();
  const user = useSelector(selectUserInformations);

  const fetchMaterials = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API.Materials.app_courses_materials_all}`,
        {
          params: {
            page: currentPage,
            search: debouncedSearch,
            orderBy: orderBy,
            orderDir: orderDir,
          },
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );

      if (data.status === 200 && data.errorCode === 0) {
        setReFetchData(false);
        setMaterials(data.data.items);
        setCurrentPage(data.data.current_page);
        setTotalPages(data.data.last_page);
      } else {
        showToast("Error", "Failed to fetch courses", "error");
      }
    } catch (error) {
      showToast(
        "Error",
        error.response?.data?.message || "An error occurred",
        "error"
      );
    }
  }, [currentPage, debouncedSearch, user.token, orderBy, orderDir]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // Reset to the first page on search change
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const fetchData = async () => {
      setLoader(true); // Show loader during fetching
      try {
        setMaterials([]);
        await fetchMaterials();
      } catch (error) {
        showToast("Error", error || "An error occurred", "error");
      } finally {
        setLoader(false); // Hide loader after fetching is complete
        setReFetchData(false);
      }
    };
    fetchData();
  }, [fetchMaterials, reFetchData]);

  const handleSort = (column, direction) => {
    setOrderBy(column);
    setOrderDir(direction);
  };

  return (
    <div className="min-h-screen p-2 sl:pt-0 sm:p-2 md:p-4 sl:p-6">
      <MaterialTable
        materials={materials}
        currentPage={currentPage}
        totalPages={totalPages}
        onSearchChange={setSearch}
        onPageChange={setCurrentPage}
        loader={loader}
        setReFetchData={setReFetchData}
        onSort={handleSort}
        currentOrderBy={orderBy}
        currentOrderDir={orderDir}
      />
    </div>
  );
};

export default MaterialPage;
